/* ============================================================================
 * order-tracking.js  (v3 — debug logs + boxShadow on fill)
 *
 * Powers templates/page.order-status.liquid (注文追跡ページ).
 *
 * Server renders one <tr data-line-item-id="…" data-order-id="…"> per
 * Shopify line item. This script:
 *   1. Fetches /apps/snsrakuru/orders (proxy-handler) for the logged-in
 *      customer and fills in status badge + progress bar per row.
 *   2. Wires up filter <select> and search <input>.
 *   3. Polls every 5 minutes; pauses while the tab is hidden.
 *
 * Status taxonomy mirrors snsmart-progress.js so badges look consistent
 * across the order detail and tracking pages.
 * ========================================================================== */
(function () {
  'use strict';

  // ─── Config ──────────────────────────────────────────────────────────────
  var APP_PROXY_URL = '/apps/snsrakuru/orders';
  var POLL_INTERVAL_MS = 5 * 60 * 1000;
  var REQUEST_TIMEOUT_MS = 15000;

  // ─── i18n labels ─────────────────────────────────────────────────────────
  var STATUS_LABELS = {
    pending: '待機中',
    sent: '作業中',
    in_progress: '作業中',
    processing: '作業中',
    completed: '完了',
    partial: '一部完了',
    canceled: 'キャンセル',
    cancelled: 'キャンセル',
    failed: 'エラー',
    skipped: '対象外',
    unknown: '不明',
  };

  // For the status filter dropdown we collapse synonyms to a canonical key.
  var STATUS_GROUPS = {
    pending: 'pending',
    sent: 'in_progress',
    in_progress: 'in_progress',
    processing: 'in_progress',
    completed: 'completed',
    partial: 'partial',
    canceled: 'canceled',
    cancelled: 'canceled',
    failed: 'failed',
    skipped: 'canceled',
    unknown: '',
  };

  // Inline colors — applied via fill.style so they survive CSS cache mismatches
  // and beat any leftover stylesheet rules.
  var BAR_COLORS = {
    pending:     '#cbd5e1',
    sent:        '#3b82f6',
    in_progress: '#3b82f6',
    processing:  '#3b82f6',
    completed:   '#16a34a',
    partial:     '#f59e0b',
    failed:      '#ef4444',
    canceled:    '#cbd5e1',
    cancelled:   '#cbd5e1',
    skipped:     '#cbd5e1',
    unknown:     '#cbd5e1',
  };

  var BADGE_COLORS = {
    pending:     { bg: '#fde9d4', fg: '#9a4d00' },
    sent:        { bg: '#dbeafe', fg: '#1e40af' },
    in_progress: { bg: '#dbeafe', fg: '#1e40af' },
    processing:  { bg: '#dbeafe', fg: '#1e40af' },
    completed:   { bg: '#dcfce7', fg: '#166534' },
    partial:     { bg: '#fef3c7', fg: '#92400e' },
    failed:      { bg: '#fee2e2', fg: '#b91c1c' },
    canceled:    { bg: '#d1d5db', fg: '#4b5563' },
    cancelled:   { bg: '#d1d5db', fg: '#4b5563' },
    skipped:     { bg: '#f3f4f6', fg: '#9ca3af' },
    unknown:     { bg: '#f3f4f6', fg: '#6b7280' },
  };

  // ─── Fetch helper ────────────────────────────────────────────────────────
  function fetchJson(url) {
    if (typeof AbortController === 'undefined') {
      return fetch(url, { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        });
    }
    var ctl = new AbortController();
    var t = setTimeout(function () { ctl.abort(); }, REQUEST_TIMEOUT_MS);
    return fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: ctl.signal,
    }).then(function (r) {
      clearTimeout(t);
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    }, function (e) {
      clearTimeout(t);
      throw e;
    });
  }

  // ─── DOM helpers ─────────────────────────────────────────────────────────
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  // ─── Badge / progress rendering per row ──────────────────────────────────
  function applyItemToRow(row, item) {
    var status = (item && item.status) || 'unknown';
    var label = STATUS_LABELS[status] || status;
    var quantity = item && typeof item.quantity === 'number' ? item.quantity : null;
    var completed = item && typeof item.completed === 'number' ? item.completed : null;
    var percent = item && typeof item.progress_percent === 'number'
      ? Math.max(0, Math.min(100, item.progress_percent))
      : 0;

    row.dataset.status = STATUS_GROUPS[status] || '';

    // Badge — colors set inline so they don't depend on the CSS being current.
    var badge = row.querySelector('.order-tracking__badge');
    if (badge) {
      badge.className = 'order-tracking__badge order-tracking__badge--' + status;
      badge.textContent = label;
      var bColor = BADGE_COLORS[status] || BADGE_COLORS.unknown;
      badge.style.background = bColor.bg;
      badge.style.color = bColor.fg;
    }

    // Progress numbers
    var nums = row.querySelector('.order-tracking__progress-numbers');
    var pct  = row.querySelector('.order-tracking__progress-percent');
    if (nums) {
      if (quantity != null && completed != null) {
        nums.textContent = completed + ' / ' + quantity;
      } else if (quantity != null) {
        nums.textContent = '― / ' + quantity;
      } else {
        nums.textContent = '―';
      }
    }
    if (pct) pct.textContent = percent + '%';

    // Bar fill — color set inline (beats CSS specificity / cache).
    var bar  = row.querySelector('.order-tracking__bar');
    var fill = row.querySelector('.order-tracking__bar-fill');
    var color = BAR_COLORS[status] || BAR_COLORS.unknown;
    if (fill) {
      fill.className = 'order-tracking__bar-fill order-tracking__bar-fill--' + status;
      // base.css has `div:empty { display:none }` — force display so empty fill stays visible
      fill.style.display = 'block';
      fill.style.width = percent + '%';
      fill.style.background = color;
      fill.style.boxShadow = percent > 0 ? '0 0 6px ' + color + '88' : 'none';
    }
    if (bar) bar.setAttribute('aria-valuenow', String(percent));

    // Debug: visible in console for sanity-checking status mapping.
    if (window && window.console) {
      console.log('[order-tracking]',
        row.getAttribute('data-order-name') || row.getAttribute('data-order-id'),
        'line:', row.getAttribute('data-line-item-id'),
        'status:', status,
        'percent:', percent,
        'color:', color);
    }
  }

  function applyMissingRow(row) {
    // Row exists in Shopify but no order_mapping in our DB (skipped item etc.)
    row.dataset.status = '';
    var badge = row.querySelector('.order-tracking__badge');
    if (badge) {
      badge.className = 'order-tracking__badge order-tracking__badge--skipped';
      badge.textContent = '対象外';
      badge.style.background = BADGE_COLORS.skipped.bg;
      badge.style.color = BADGE_COLORS.skipped.fg;
    }
    var nums = row.querySelector('.order-tracking__progress-numbers');
    var pct  = row.querySelector('.order-tracking__progress-percent');
    if (nums) nums.textContent = '―';
    if (pct)  pct.textContent  = '―';
    var fill = row.querySelector('.order-tracking__bar-fill');
    if (fill) {
      fill.className = 'order-tracking__bar-fill';
      fill.style.display = 'block';
      fill.style.width = '0%';
      fill.style.background = BAR_COLORS.skipped;
    }
  }

  function applyErrorRow(row) {
    var badge = row.querySelector('.order-tracking__badge');
    if (badge) {
      badge.className = 'order-tracking__badge order-tracking__badge--failed';
      badge.textContent = '取得失敗';
      badge.style.background = BADGE_COLORS.failed.bg;
      badge.style.color = BADGE_COLORS.failed.fg;
    }
  }

  // ─── Main flow ───────────────────────────────────────────────────────────
  function buildItemIndex(payload) {
    // payload shape: { orders: [{ order_id, order_number, items: [...] }, ...] }
    var byLineItemId = {};
    if (payload && Array.isArray(payload.orders)) {
      for (var i = 0; i < payload.orders.length; i++) {
        var o = payload.orders[i];
        var items = (o && o.items) || [];
        for (var j = 0; j < items.length; j++) {
          var it = items[j];
          if (it && it.line_item_id != null) {
            byLineItemId[String(it.line_item_id)] = it;
          }
        }
      }
    }
    return byLineItemId;
  }

  function refresh(rows) {
    return fetchJson(APP_PROXY_URL + '?_t=' + Date.now())
      .then(function (json) {
        var byId = buildItemIndex(json);
        for (var i = 0; i < rows.length; i++) {
          var r = rows[i];
          var id = r.getAttribute('data-line-item-id');
          var hit = id ? byId[id] : null;
          if (hit) applyItemToRow(r, hit);
          else applyMissingRow(r);
        }
        applyFilter(rows);
      })
      .catch(function () {
        for (var i = 0; i < rows.length; i++) applyErrorRow(rows[i]);
      });
  }

  // ─── Filtering ───────────────────────────────────────────────────────────
  var filterState = { status: '', search: '' };

  function applyFilter(rows) {
    var emptyMsg = document.querySelector('.order-tracking__empty-search');
    var visible = 0;
    var status = filterState.status;
    var query = filterState.search.trim().toLowerCase();
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      var rowStatus = r.dataset.status || '';
      var rowSearch = r.dataset.search || '';
      var statusOk = !status || rowStatus === status;
      var searchOk = !query || rowSearch.indexOf(query) !== -1;
      var show = statusOk && searchOk;
      if (show) {
        r.removeAttribute('hidden');
        visible++;
      } else {
        r.setAttribute('hidden', '');
      }
    }
    if (emptyMsg) {
      if (visible === 0) emptyMsg.removeAttribute('hidden');
      else emptyMsg.setAttribute('hidden', '');
    }
  }

  function wireFilters(rows) {
    var statusEl = document.querySelector('[data-tracking-filter="status"]');
    var searchEl = document.querySelector('[data-tracking-filter="search"]');
    if (statusEl) {
      statusEl.addEventListener('change', function () {
        filterState.status = statusEl.value || '';
        applyFilter(rows);
      });
    }
    if (searchEl) {
      var debounce;
      searchEl.addEventListener('input', function () {
        clearTimeout(debounce);
        debounce = setTimeout(function () {
          filterState.search = searchEl.value || '';
          applyFilter(rows);
        }, 120);
      });
    }
  }

  // ─── Init ────────────────────────────────────────────────────────────────
  function init() {
    var rows = $$('.order-tracking__row');
    if (rows.length === 0) return;

    wireFilters(rows);
    refresh(rows);

    var pollHandle = null;
    function start() {
      if (pollHandle != null) return;
      pollHandle = setInterval(function () {
        if (document.hidden) return;
        refresh(rows);
      }, POLL_INTERVAL_MS);
    }
    function stop() {
      if (pollHandle == null) return;
      clearInterval(pollHandle);
      pollHandle = null;
    }
    start();

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else { refresh(rows); start(); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
// build 1778228084
